import {
  VITALS_DEVICES,
  DEFAULT_VITALS_SOURCE_PRIORITY,
  DEFAULT_VITALS_EXCEPTION_SOURCE_PRIORITY,
  VITALS_PRIORITY_EXCEPTION_TYPES,
  DEFAULT_VITALS_EXCLUDED_SOURCES,
  POD_VITALS_SOURCE_PRIORITY,
} from '../constants';

const getPriorityConfigForPod = (podName) => {
  const override = podName ? POD_VITALS_SOURCE_PRIORITY[podName] : null;
  return {
    defaultOrder: override?.default || DEFAULT_VITALS_SOURCE_PRIORITY,
    exceptionTypes: override?.exceptions || VITALS_PRIORITY_EXCEPTION_TYPES,
    exceptionOrder: override?.exceptionPriority || DEFAULT_VITALS_EXCEPTION_SOURCE_PRIORITY,
    excludedSources: override?.excludedSources || DEFAULT_VITALS_EXCLUDED_SOURCES,
  };
};

// Merges per-device vitals slices into one list, keeping a single entry per
// measurement type. Most types are chosen by source priority (pod-configurable).
// Types in exceptionTypes (heart rate + blood pressure) instead use whichever
// device most recently reported that type — latestVitalsSource[type], set in
// the same SET_DEVICE_MEASURES dispatch that adds the measure to
// measuresByDevice, so it's always populated by the time it's read here.
// Sources in excludedSources are dropped entirely for this event type (they
// never win, even as a fallback) — this does not affect other event types
// from the same device, e.g. mergeDeviceVisualMedia below.
export const mergeDeviceMeasures = (measuresByDevice, podName, latestVitalsSource = {}) => {
  const { defaultOrder, exceptionTypes, excludedSources } = getPriorityConfigForPod(podName);
  const picked = new Map(); // measurementType -> { measure, rank }

  VITALS_DEVICES.filter((device) => !excludedSources.includes(device)).forEach((device) => {
    (measuresByDevice[device] || []).forEach((measure) => {
      if (exceptionTypes.includes(measure.name)) {
        if (latestVitalsSource[measure.name] === device) picked.set(measure.name, { measure });
        return;
      }

      const rankIndex = defaultOrder.indexOf(device);
      const rank = rankIndex === -1 ? defaultOrder.length : rankIndex; // unlisted device = lowest priority, still shown
      const existing = picked.get(measure.name);
      if (!existing || rank < existing.rank) {
        picked.set(measure.name, { measure, rank });
      }
    });
  });

  return Array.from(picked.values()).map(({ measure }) => measure);
};

// Merges freshly-received measurements for one device into that device's
// existing slice: entries sharing a measurement name are updated in place,
// new names are appended, and names the new event didn't mention are kept —
// a device's Vitals events aren't guaranteed to be a full snapshot each time.
export const mergeMeasurementsByName = (existingMeasures, incomingMeasures) => {
  const merged = new Map((existingMeasures || []).map((measure) => [measure.name, measure]));
  (incomingMeasures || []).forEach((measure) => merged.set(measure.name, measure));
  return Array.from(merged.values());
};

// Same priority resolution as mergeDeviceMeasures, but for visualMediaByDevice
// ({ [device]: { [measurementType]: media } }) instead of an array per device.
export const mergeDeviceVisualMedia = (visualMediaByDevice, podName) => {
  const { defaultOrder, exceptionTypes, exceptionOrder } = getPriorityConfigForPod(podName);
  const picked = new Map(); // measurementType -> { entry, rank }

  VITALS_DEVICES.forEach((device) => {
    const deviceMedia = visualMediaByDevice[device] || {};
    Object.keys(deviceMedia).forEach((measurementType) => {
      const order = exceptionTypes.includes(measurementType) ? exceptionOrder : defaultOrder;
      const rankIndex = order.indexOf(device);
      const rank = rankIndex === -1 ? order.length : rankIndex;
      const existing = picked.get(measurementType);
      if (!existing || rank < existing.rank) {
        picked.set(measurementType, { entry: { name: measurementType, media: deviceMedia[measurementType] }, rank });
      }
    });
  });

  return Array.from(picked.values()).map(({ entry }) => entry);
};
